import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type StatsPeriod = '24h' | '7d' | '15d' | '30d' | '60d' | '90d';

interface TimeSeriesData {
  date: string;
  count: number;
}

interface TokensData {
  date: string;
  input: number;
  output: number;
  total: number;
}

interface PlanData {
  plan: string;
  count: number;
  percentage: number;
}

interface PlansAdoptionData {
  date: string;
  [key: string]: number | string;
}

interface AdminStatsData {
  users: TimeSeriesData[];
  conversations: TimeSeriesData[];
  documents: TimeSeriesData[];
  casos: TimeSeriesData[];
  plans: PlanData[];
  plansAdoption: PlansAdoptionData[];
  tokens: TokensData[];
  online: TimeSeriesData[];
  totals: {
    users: number;
    conversations: number;
    documents: number;
    casos: number;
    totalTokens: number;
  };
}

export const useAdminStatistics = () => {
  const [period, setPeriod] = useState<StatsPeriod>('30d');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-statistics', period],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('get-admin-stats', {
        body: { period },
      });

      if (error) throw error;
      return data as AdminStatsData;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });

  return {
    period,
    setPeriod,
    data,
    isLoading,
    error,
    refetch,
  };
};
